import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { SUPPORTED_LANGUAGES, setLanguage, LANGUAGE_STORAGE_KEY } from '@/lib/i18n';
import { isDesktop } from '@/lib/fileSystem';

const DISMISS_KEY = 'scrivar_language_banner_dismissed';

/**
 * Auto-detect banner for web. Compares the current i18n language to
 * navigator.language; if they differ and the user hasn't chosen a language
 * yet (no `scrivar_preferred_language` in localStorage), offers to switch.
 *
 * Dismissal is sticky across reloads. Hidden on desktop (handled by Setup).
 */
export default function LanguageDetectedBanner() {
  const { t, i18n } = useTranslation();
  const [show, setShow] = useState(false);
  const [detected, setDetected] = useState(null);

  useEffect(() => {
    if (isDesktop()) return;
    try {
      if (window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) return;
      if (window.localStorage.getItem(DISMISS_KEY)) return;
    } catch {
      return;
    }
    const nav = (window.navigator?.language || 'en').toLowerCase().split('-')[0];
    const match = SUPPORTED_LANGUAGES.find((l) => l.code === nav);
    if (!match || match.code === i18n.language) return;
    setDetected(match);
    setShow(true);
  }, [i18n.language]);

  const accept = () => {
    if (!detected) return;
    setLanguage(detected.code);
    setShow(false);
  };

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, '1'); } catch {}
    setShow(false);
  };

  if (!show || !detected) return null;

  return (
    <div className="w-full bg-indigo-50 border-b border-indigo-100 px-4 py-2.5 flex items-center gap-3 text-sm">
      <span className="flex-1 text-gray-700">
        {t('common:language.detected_banner', { language: detected.native })}
      </span>
      <button
        onClick={accept}
        className="px-3 py-1 rounded-md text-white text-xs font-medium"
        style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
      >
        {t('common:language.use_language', { language: detected.native })}
      </button>
      <button
        onClick={dismiss}
        className="px-2 py-1 rounded-md text-gray-600 hover:bg-indigo-100 text-xs font-medium"
      >
        {t('common:language.keep_english')}
      </button>
      <button onClick={dismiss} aria-label="Dismiss" className="text-gray-400 hover:text-gray-600">
        <X size={16} />
      </button>
    </div>
  );
}
